import { useToast } from "@/hooks/use-toast";
import { archiveOfferPage } from "@/services/offerPageService";
import { archiveProduct } from "@/services/productMutations";
import { archiveSubscription } from "@/data/subscriptionMutations";
import {
  getPaymentUrl,
  getOfferUrl,
  getSubscriptionPreviewUrl,
} from "@/config/urls";
import { useGlobalEvents } from "@/hooks/useGlobalEvents";

export const useItemActions = () => {
  const { toast } = useToast();
  const { dispatchCustomEvent } = useGlobalEvents();

  const handleCopyLink = (id: string, type: string) => {
    let link = "";
    if (import.meta.env.VITE_ENV === "production") {
      switch (type) {
        case "product":
          link = `${import.meta.env.VITE_PAYMENT_BASE_URL}/${id}`;
          break;
        case "subscription":
          link = `${import.meta.env.VITE_PAYMENT_BASE_URL}/subscription/${id}`;
          break;
        case "offer":
        case "bundle":
          link = `${import.meta.env.VITE_PAYMENT_BASE_URL}/bundle/${id}`;
          break;
        default:
          link = getPaymentUrl(id);
      }
    } else {
      switch (type) {
        case "product":
          link = `${import.meta.env.VITE_BASE_URL}/${id}`;
          break;
        case "subscription":
          link = `${import.meta.env.VITE_BASE_URL}/subscription/${id}`;
          break;
        case "offer":
        case "bundle":
          link = `${import.meta.env.VITE_BASE_URL}/bundle/${id}`;
          break;
        default:
          link = getPaymentUrl(id);
      }
    }
    navigator.clipboard.writeText(link);
    toast({
      title: "Link copied!",
      description: "The payment link has been copied to your clipboard.",
    });

    // Dispatch event for onboarding checklist
    dispatchCustomEvent("copy-link-success", { type, id });
  };

  const handleArchive = async (id: string, type: string) => {
    try {
      console.log(`Attempting to archive ${type} with ID: ${id}`);

      // Handle offer pages
      if (type === "offer") {
        console.log("Archiving offer page:", id);
        const success = await archiveOfferPage(id);
        if (success) {
          toast({
            title: "Bundle page archived",
            description: "The bundle page has been archived successfully.",
          });
          return true;
        } else {
          toast({
            title: "Error",
            description: "Failed to archive bundle page. Please try again.",
            variant: "destructive",
          });
          return false;
        }
      }
      // Handle products
      else if (type === "product") {
        const success = await archiveProduct(id, "archived");
        if (success) {
          toast({
            title: "Product archived",
            description: "The product has been archived successfully.",
          });
          return true;
        } else {
          toast({
            title: "Error",
            description: "Failed to archive product. Please try again.",
            variant: "destructive",
          });
          return false;
        }
      }
      // Handle subscriptions
      else if (type === "subscription") {
        console.log("Archiving subscription:", id);
        const success = await archiveSubscription(id);
        if (success) {
          toast({
            title: "Subscription archived",
            description: "The subscription has been archived successfully.",
          });
          return true;
        } else {
          toast({
            title: "Error",
            description: "Failed to archive subscription. Please try again.",
            variant: "destructive",
          });
          return false;
        }
      }
      // Handle other types
      else {
        toast({
          title: "Item archived",
          description: `The ${type} has been archived.`,
        });
        return true;
      }
    } catch (error) {
      console.error(`Error archiving ${type}:`, error);
      toast({
        title: "Error",
        description:
          "An unexpected error occurred while trying to archive the item.",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    handleCopyLink,
    handleArchive,
  };
};
